import { useCallback } from 'react';
import { useAxios } from '../../hooks/useAxios';
import { NUMBER_REGEX } from '../Constants';

const useTranslateController = () => {
  // translation API
  const { callAxios: translate } = useAxios({
    callOnMount: false,
    method: 'post',
    url: 'https://api.golocad.com/translation/translate',
  });

  // extracting text nodes and setting required attributes
  const extractAndTransformText = useCallback((node, finalNodes, useTranslation) => {
    // if the node has data-use-translation attribute, use the value
    if (node instanceof Element && node.hasAttribute('data-use-translation')) {
      useTranslation = JSON.parse(node.getAttribute('data-use-translation'));
    }

    // if the node is mui date picker, don't translate
    if (node instanceof Element && node.classList.contains('MuiPickersLayout-root')) {
      useTranslation = false;
    }

    // if the node has data-merge-content attribute, merge all text content
    if (node instanceof Element && node.hasAttribute('data-merge-content')) {
      // For nodes with data-merge-content, merge all text content
      const textNodes = [];
      const extractTextNodesRecursively = (node, nodes) => {
        if (node.nodeType === Node.TEXT_NODE) {
          nodes.push(node);
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          Array.from(node.childNodes).forEach((child) => extractTextNodesRecursively(child, nodes));
        }
      };
      Array.from(node.childNodes).forEach((child) => {
        extractTextNodesRecursively(child, textNodes);
      });

      const mergedContent = textNodes
        .map((n) => n.textContent.trim())
        .join(' ')
        .replace(/\s+/g, ' ');

      // Create a single text node with merged content
      const mergedNode = document.createTextNode(mergedContent);
      node.innerHTML = '';

      node.appendChild(mergedNode);

      // Continue processing with merged node
      extractAndTransformText(mergedNode, finalNodes, useTranslation);
      return;
    }

    if (node.nodeType === Node.TEXT_NODE) {
      const parent = node.parentNode;
      const textContent = node.textContent;

      // if the parent node has data-text-translated attribute, and the text content is different, then translate
      if (
        parent &&
        (!parent.hasAttribute('data-text-translated') || parent.getAttribute('data-text-translated') !== textContent)
      ) {
        if (useTranslation && !NUMBER_REGEX.test(textContent)) {
          parent.style.filter = 'blur(8px)';
          finalNodes.push([node, parent]);
        }
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      // if the node is an element, then recursively call the function for each child node
      Array.from(node.childNodes).forEach((child) => extractAndTransformText(child, finalNodes, useTranslation));
    }
  }, []);

  const extractRemovedTagNodes = useCallback((node, finalNodes, useTranslation = true) => {
    // if the node has data-use-translation attribute, use the value
    if (node instanceof Element && node.hasAttribute('data-use-translation')) {
      useTranslation = JSON.parse(node.getAttribute('data-use-translation'));
    }

    // if the node is mui date picker, don't translate
    if (node instanceof Element && node.classList.contains('MuiPickersLayout-root')) {
      useTranslation = false;
    }

    // if the parent node has data-text-translated attribute, and the text content is different, then remove the attribute
    if (
      node.nodeType === Node.TEXT_NODE &&
      node.parentNode.hasAttribute('data-text-translated') &&
      node.parentNode.getAttribute('data-text-translated') !== node.textContent
    ) {
      node.parentNode.removeAttribute('data-text-translated');
      if (useTranslation) {
        node.parentNode.style.filter = 'blur(8px)';
        finalNodes.push([node, node.parentNode]);
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      Array.from(node.childNodes).forEach((child) => extractRemovedTagNodes(child, finalNodes, useTranslation));
    }
  }, []);

  // removing translation tags and calling api for updating dom with new data
  const retriggerTranslation = useCallback(
    (query, targetLang = 'zh', children = [], isChild = false) => {
      if (query && targetLang !== 'en') {
        const nodes = isChild ? children : document.querySelectorAll(query);
        if (nodes?.length) {
          const removedTagsNodes = [];
          nodes.forEach((node) => {
            extractRemovedTagNodes(node, removedTagsNodes);
          });
          const texts = removedTagsNodes.map(([node, _]) => node.textContent);
          if (texts?.length) {
            translate({ data: { texts, target_lang: targetLang } }).then((data) => {
              removedTagsNodes.forEach(([node, parent]) => {
                node.textContent = data[node.textContent] || node.textContent;
                parent.setAttribute('data-text-translated', node.textContent);
                parent.style.filter = 'none';
              });
            });
          }
        }
      }
    },
    [extractRemovedTagNodes, translate]
  );

  // getting the value of the data-use-translation attribute
  const getTranslationValue = useCallback((node) => {
    while (node) {
      if (node instanceof Element && node.hasAttribute('data-use-translation')) {
        return JSON.parse(node.getAttribute('data-use-translation'));
      }
      node = node.parentNode;
    }
    return true;
  }, []);

  return { translate, extractAndTransformText, retriggerTranslation, getTranslationValue };
};

export default useTranslateController;
